import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Droplet, BookOpen, Utensils, HeartPulse } from "lucide-react";

// Rough illustrative ratios — admins can refine
const IMPACTS = [
  { icon: Utensils, label: "meals for a family", per: 2 },
  { icon: BookOpen, label: "school supply kits", per: 15 },
  { icon: Droplet, label: "days of clean water", per: 5 },
  { icon: HeartPulse, label: "school days funded", per: 8 },
];

export function ImpactCalculator() {
  const [amount, setAmount] = useState(15);

  const items = useMemo(
    () =>
      IMPACTS.map((i) => ({
        ...i,
        count: Math.max(1, Math.floor(amount / i.per)),
      })),
    [amount]
  );

  return (
    <section className="bg-muted/40 py-16">
      <div className="container mx-auto max-w-4xl px-4">
        <div className="text-center">
          <p className="text-sm font-medium uppercase tracking-wider text-primary">See your impact</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight md:text-4xl">
            ${amount} can do this much good
          </h2>
        </div>

        <div className="mx-auto mt-8 max-w-lg">
          <Slider
            min={15}
            max={1000}
            step={5}
            value={[amount]}
            onValueChange={(v) => setAmount(v[0])}
            thumbAriaLabel="Donation amount in US dollars"
          />
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>$15</span>
            <span>$1,000</span>
          </div>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <div
              key={it.label}
              className="rounded-2xl border bg-background p-5 text-center shadow-sm"
            >
              <it.icon className="mx-auto h-8 w-8 text-primary" />
              <div className="mt-3 font-display text-3xl font-semibold">{it.count}</div>
              <div className="mt-1 text-sm text-muted-foreground">{it.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button asChild size="lg" className="rounded-full">
            <Link to={`/donate?amount=${amount}`}>Donate ${amount} now</Link>
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Estimates based on average project costs. 100% of your gift funds the work.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Want to give your birthday instead?{" "}
            <Link to="/birthday" className="font-semibold text-primary underline-offset-4 hover:underline">
              Pledge it →
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
